import { z } from 'zod';
import { ObligationStatus } from './checks.js';
/**
 * Obligations of the firm as the obligation catalogue derives them (NEONEWTON 4, ADR-004 §7).
 * Every derivation is kept as an assessment: which rule pack, which inputs, which result -
 * so that a past answer can always be traced back (ADR-003, `check_run`).
 */
/** What a rule said about a case (ADR-004, evaluation order). */
export const RuleOutcome = z
    .enum([
    'not_in_force',
    'not_relevant',
    'unknown',
    'contradiction',
    'applies',
    'not_applies',
    'evaluation_error',
])
    .meta({ id: 'RuleOutcome' });
/**
 * How an obligation came about. Only `derived` carries a status; the others say why there is
 * none - which is never the same as "not applicable" (unknown is not no).
 */
export const ObligationState = z
    .enum([
    /** At least one rule set the obligation. */
    'derived',
    /** The catalogue has rules for it, but none concerned this firm. */
    'no_rule_applied',
    /** The catalogue lists it, but no approved rule exists yet. */
    'not_covered',
    /** A rule for it could not be evaluated - a technical error, never a quiet result. */
    'evaluation_error',
])
    .meta({ id: 'ObligationState' });
export const RuleReason = z
    .object({ ruleId: z.string(), ruleVersion: z.string(), outcome: RuleOutcome })
    .meta({ id: 'RuleReason' });
export const ObligationView = z
    .object({
    ref: z.string(),
    title: z.string(),
    source: z.string(),
    state: ObligationState,
    /**
     * What the rules derived: always with `derived`; with `evaluation_error` what the evaluable
     * rules derived, possibly incomplete; otherwise `null`.
     */
    status: ObligationStatus.nullable(),
    /** The rules of the catalogue for this obligation and what each said. */
    reasons: z.array(RuleReason),
})
    .meta({ id: 'ObligationView' });
/** A question the rules could not answer from the data - to be clarified, never assumed. */
export const Clarification = z
    .object({ key: z.string(), title: z.string(), ruleId: z.string() })
    .meta({ id: 'Clarification' });
export const RulePackRef = z
    .object({ name: z.string(), version: z.string(), digest: z.string() })
    .meta({ id: 'RulePackRef' });
/** Why an assessment no longer reflects the present. */
export const StaleReason = z.enum(['inputs_changed', 'rules_changed']).meta({ id: 'StaleReason' });
export const ObligationAssessment = z
    .object({
    id: z.uuid(),
    assessedAt: z.iso.datetime({ offset: true }),
    pack: RulePackRef,
    obligations: z.array(ObligationView),
    clarifications: z.array(Clarification),
})
    .meta({ id: 'ObligationAssessment' });
/** `GET /api/v1/firm/obligations`: the latest assessment and whether it is still current. */
export const FirmObligations = z
    .object({
    /** `null`: never assessed. */
    assessment: ObligationAssessment.nullable(),
    /** Empty: the assessment matches today's base data and rules. */
    stale: z.array(StaleReason),
})
    .meta({ id: 'FirmObligations' });
export const ruleOutcomeLabel = {
    not_in_force: 'nicht in Kraft',
    not_relevant: 'betrifft die Kanzlei nicht',
    unknown: 'offen',
    contradiction: 'widersprüchlich',
    applies: 'trifft zu',
    not_applies: 'trifft nicht zu',
    evaluation_error: 'nicht auswertbar',
};
export const obligationStateLabel = {
    derived: 'ermittelt',
    no_rule_applied: 'keine Regel trifft auf Ihre Kanzlei zu',
    not_covered: 'noch nicht von Kanos geprüft',
    evaluation_error: 'Regel nicht auswertbar',
};
//# sourceMappingURL=obligations.js.map